import {
  GridActionsCellItem,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridSortModel,
} from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { ITEMS_PER_PAGE } from "../../ui/LazyDataGrid/common";
import { Obrat } from "../../../ts/Obraty";
import { LazyDataGrid } from "../../ui/LazyDataGrid/LazyDataGrid";
import { MultiLineCell } from "../../ui/LazyDataGrid/MultiLineCell";
import { useQueryWithPopupErrorHandling } from "../../../providers/utils/hooks";
import { obratModel } from "../../../api/models/obratModel";
import { Filter } from "./Obraty";
import { BiEdit, BiSave, BiTrash, BiX } from "react-icons/bi";

interface IProps {
  filterCriteria: Filter[];
}

export const OldObratyTable = ({
  filterCriteria,
}: IProps): React.ReactElement<IProps> => {
  const [oldSortModel, setOldSortModel] = useState<GridSortModel>([]);
  const [oldPage, setOldPage] = useState(0);
  const [oldPageSize, setOldPageSize] = useState(ITEMS_PER_PAGE);
  const [isLoading, setIsLoading] = useState(true);
  const [oldDataTotalCount, setOldDataTotalCount] = useState<number>(0);
  const [oldData, setOldData] = useState<Obrat[]>([]);
  const { call } = useQueryWithPopupErrorHandling();

  const getObraty = async () => {
    setIsLoading(true);
    await call(
      () =>
        obratModel.getFilteredObraty({
          offset: (oldPage * oldPageSize).toString(),
          limit: oldPageSize.toString(),
          query:
            filterCriteria.length > 0
              ? JSON.stringify(
                  filterCriteria.map((f) => ({
                    kontakt: f.kontakt?.value ? f.kontakt.value : undefined,
                    skupina: f.skupina?.value ? f.skupina.value : undefined,
                    obec: f.obec?.value ? f.obec.value : undefined,
                    okres: f.okres?.value ? f.okres.value : undefined,
                    obchodnik: f.obchodnik?.value
                      ? f.obchodnik.value
                      : undefined,
                    rok: f.rok?.value ? f.rok.value : undefined,
                    mesic: f.mesic?.value ? f.mesic.value : undefined,
                  }))
                )
              : undefined,
        }),
      "Zobrazení obratov nebolo úspešné",
      null,
      ({ data, totalCount }) => {
        setOldData(data);
        setOldDataTotalCount(totalCount);
      },
      undefined,
      true
    );
    setIsLoading(false);
  };

  useEffect(() => {
    getObraty();
  }, [oldPage, filterCriteria, oldPageSize, oldSortModel]);

  const deleteObrat = async (_id: string) => {
    call(
      () => obratModel.deleteObrat({ _id }),
      "Odstránenie obratu nebolo úspešné",
      "Obrat bol úspešně odstraněn",
      () => {
        setOldData(oldData.filter((row) => row._id !== _id));
        getObraty();
      }
    );
  };

  const editObrat = async (obrat: any) => {
    call(
      () => obratModel.editObrat({ obrat }),
      "Úprava obratu nebola úspešná",
      "Obrat bol úspešně zmeněn",
      () => {
        getObraty();
      }
    );
  };

  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {}
  );

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId, row: Obrat) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });

    editObrat(row);
  };

  const handleDeleteClick = (id: GridRowId, row: Obrat) => {
    deleteObrat(row._id);
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = oldData.find((row) => row._id === id);
    if (editedRow!.isNew) {
      setOldData(oldData.filter((row) => row._id !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel<Obrat>) => {
    const updatedRow = { ...newRow, isNew: false };
    setOldData(
      oldData.map((row) => (row._id === newRow._id ? updatedRow : row))
    );
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  return (
    <LazyDataGrid<Obrat>
      id="old-obraty"
      rows={oldData}
      isLoading={isLoading}
      totalCount={oldDataTotalCount}
      sortModel={oldSortModel}
      onSortModelChange={setOldSortModel}
      onPageChange={setOldPage}
      onPageSizeChange={setOldPageSize}
      editMode="row"
      rowModesModel={rowModesModel}
      onRowModesModelChange={handleRowModesModelChange}
      onRowEditStop={handleRowEditStop}
      processRowUpdate={processRowUpdate}
      columns={[
        {
          field: "id_klienta",
          headerName: "ID - KLIENT",
          width: 90,
        },
        {
          field: "kontakt",
          headerName: "SPOLOČNOSŤ",
          width: 300,
          renderCell: ({ row }) => (
            <MultiLineCell>{row.kontakt?.nazev ?? ""}</MultiLineCell>
          ),
        },
        {
          field: "rok",
          headerName: "ROK",
          editable: true,
          type: "number",
          headerAlign: "left",
          align: "left",
          valueFormatter: (v) => v,
        },
        {
          field: "mesiac",
          headerName: "MESIAC",
          headerAlign: "left",
          align: "left",
          editable: true,
          type: "number",
        },
        {
          field: "suma",
          headerName: "SPOLU",
          flex: 1,
          editable: true,
          headerAlign: "left",
          align: "left",
          type: "number",
        },
        {
          field: "actions",
          type: "actions",
          headerName: "",
          width: 100,
          cellClassName: "actions",
          getActions: ({ id, row }) => {
            const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

            if (isInEditMode) {
              return [
                <GridActionsCellItem
                  icon={<BiSave size="24" />}
                  label="Save"
                  material={{
                    sx: {
                      color: "primary.main",
                    },
                  }}
                  onClick={() => handleSaveClick(id, row)}
                />,
                <GridActionsCellItem
                  icon={<BiX size="24" />}
                  label="Cancel"
                  className="textPrimary"
                  onClick={handleCancelClick(id)}
                  color="inherit"
                />,
              ];
            }

            return [
              <GridActionsCellItem
                icon={<BiEdit size="24" />}
                label="Edit"
                className="textPrimary"
                onClick={handleEditClick(id)}
                color="inherit"
              />,
              <GridActionsCellItem
                icon={<BiTrash size="24" />}
                label="Delete"
                onClick={() => handleDeleteClick(id, row)}
                color="inherit"
              />,
            ];
          },
        },
      ]}
    />
  );
};
